/**
 * Report Page — print-optimized assessment report rendered in the frontend.
 *
 * Uses browser's native print functionality (Ctrl+P / window.print()) to
 * generate PDFs. Renders outside the DashboardLayout so the sidebar/navbar
 * don't appear in print output.
 *
 * Data is fetched from GET /api/reports/assessments/:id/data.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { resultsApi } from '@/features/results/api'
import './report-page.css'

// ── Types ────────────────────────────────────────────────────────────────────

interface OrgData {
  name: string
  period: string
  completed: string
  generated: string
  description: string
  industry: string
  size: string
  address: string
  website: string
  created: string
  created_by: string
  overall_level: number
  overall_name: string
  industry_target: number
  gap: number
  maturity_desc: string
  next_level_name: string
  comparison_target?: string
  comparison_label?: string
}

interface DomainData {
  name: string
  level: number
  level_name: string
  level_pct: number
  benchmark: number
  bench_pct: number
  gap: 'Met' | 'Close' | 'Below'
}

interface InitiativePhase {
  num?: number
  name: string
  desc: string
  duration: string
}

interface InitiativeData {
  title: string
  subtitle: string
  domain: string
  priority: string
  pc_color: string
  from_level: number
  to_level: number
  risk: string
  description: string
  business_value: string
  duration: string
  team: string
  impact: string
  effort: string
  budget: string
  roi: string
  technologies: string[]
  success_metrics: string[]
  tools: string[]
  phases: InitiativePhase[]
}

interface QnaQuestion {
  question: string
  type: string
  level: number
  weight: number
  answer: string
  score: number
  justification?: string
  justification_label?: string
  justification_yes?: string | null
  justification_no?: string | null
  recommendation?: string
}

interface QnaDomain {
  desc: string
  questions: QnaQuestion[]
}

interface ChartData {
  labels: string[]
  achieved: number[]
  benchmarks: number[]
}

interface ReportData {
  org: OrgData
  domains: DomainData[]
  initiatives: InitiativeData[]
  qna: Record<string, QnaDomain>
  chart_data: ChartData
}

// ── Section visibility defaults ──────────────────────────────────────────────

const DEFAULT_SECTIONS = {
  description: true,
  duration: true,
  team_size: true,
  impact: true,
  effort: true,
  business_value: true,
  budget_range: true,
  roi_estimate: true,
  technologies: true,
  success_metrics: true,
  recommended_tools: true,
  phases: true,
}

// ── SVG Chart helpers ────────────────────────────────────────────────────────

function RadarChartSVG({ data, comparisonLabel }: { data: ChartData; comparisonLabel?: string }) {
  const cx = 160, cy = 160, maxR = 120
  const n = data.labels.length
  if (n < 3) return null

  const angleStep = (2 * Math.PI) / n
  const startAngle = -Math.PI / 2

  const pointOnCircle = (angle: number, r: number) => ({
    x: cx + r * Math.cos(angle),
    y: cy + r * Math.sin(angle),
  })

  // Grid rings
  const rings = [1, 2, 3, 4, 5]
  const gridLines = rings.map(level => {
    const r = (level / 5) * maxR
    const pts = Array.from({ length: n }, (_, i) => {
      const p = pointOnCircle(startAngle + i * angleStep, r)
      return `${p.x},${p.y}`
    }).join(' ')
    return <polygon key={level} points={pts} fill="none" stroke="#E2E8F0" strokeWidth="1" />
  })

  // Axis lines
  const axes = Array.from({ length: n }, (_, i) => {
    const p = pointOnCircle(startAngle + i * angleStep, maxR)
    return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#E2E8F0" strokeWidth="1" />
  })

  // Achieved polygon
  const achievedPts = data.achieved.map((v, i) => {
    const r = (v / 5) * maxR
    const p = pointOnCircle(startAngle + i * angleStep, r)
    return `${p.x},${p.y}`
  }).join(' ')

  // Benchmark polygon
  const benchPts = data.benchmarks.map((v, i) => {
    const r = (v / 5) * maxR
    const p = pointOnCircle(startAngle + i * angleStep, r)
    return `${p.x},${p.y}`
  }).join(' ')

  // Labels
  const labels = data.labels.map((label, i) => {
    const p = pointOnCircle(startAngle + i * angleStep, maxR + 18)
    const anchor = Math.abs(p.x - cx) < 5 ? 'middle' : p.x > cx ? 'start' : 'end'
    return (
      <text key={i} x={p.x} y={p.y} textAnchor={anchor} dominantBaseline="central"
        style={{ fontSize: '10px', fontWeight: 600, fill: '#0F172A', fontFamily: 'Inter, sans-serif' }}>
        {label}
      </text>
    )
  })

  // Level numbers on the right axis (axis 0 points up)
  const levelLabels = rings.map(level => {
    const r = (level / 5) * maxR
    return (
      <text key={level} x={cx + 4} y={cy - r + 3}
        style={{ fontSize: '9px', fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}>
        {level}
      </text>
    )
  })

  return (
    <svg viewBox="0 0 320 340" style={{ width: '100%', maxHeight: 280 }}>
      {gridLines}
      {axes}
      <polygon points={benchPts} fill="rgba(100,110,120,0.08)" stroke="#646E78"
        strokeWidth="2" strokeDasharray="5 5" />
      <polygon points={achievedPts} fill="rgba(28,168,225,0.25)" stroke="#1CA8E1" strokeWidth="2" />
      {/* Achieved dots */}
      {data.achieved.map((v, i) => {
        const r = (v / 5) * maxR
        const p = pointOnCircle(startAngle + i * angleStep, r)
        return <circle key={i} cx={p.x} cy={p.y} r="4" fill="#1CA8E1" />
      })}
      {/* Benchmark dots */}
      {data.benchmarks.map((v, i) => {
        const r = (v / 5) * maxR
        const p = pointOnCircle(startAngle + i * angleStep, r)
        return <circle key={i} cx={p.x} cy={p.y} r="3" fill="#646E78" />
      })}
      {labels}
      {levelLabels}
      {/* Legend */}
      <g transform="translate(40, 320)">
        <circle cx="0" cy="0" r="5" fill="#1CA8E1" />
        <text x="10" y="4" style={{ fontSize: '11px', fill: '#0F172A', fontFamily: 'Inter, sans-serif' }}>Achieved</text>
        <circle cx="100" cy="0" r="5" fill="#646E78" />
        <text x="110" y="4" style={{ fontSize: '11px', fill: '#0F172A', fontFamily: 'Inter, sans-serif' }}>{comparisonLabel || 'Benchmark'}</text>
      </g>
    </svg>
  )
}

function BarChartSVG({ data, comparisonLabel }: { data: ChartData; comparisonLabel?: string }) {
  const n = data.labels.length
  if (!n) return null

  const chartW = 280, chartH = 200
  const marginL = 30, marginB = 60, marginT = 10
  const groupW = chartW / n
  const barW = groupW * 0.35

  return (
    <svg viewBox={`0 0 ${marginL + chartW + 10} ${marginT + chartH + marginB}`}
      style={{ width: '100%', maxHeight: 280 }}>
      {/* Y axis */}
      {[0, 1, 2, 3, 4, 5].map(v => {
        const y = marginT + chartH - (v / 5) * chartH
        return (
          <g key={v}>
            <line x1={marginL} y1={y} x2={marginL + chartW} y2={y} stroke="#F1F5F9" strokeWidth="1" />
            <text x={marginL - 5} y={y + 3} textAnchor="end"
              style={{ fontSize: '10px', fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}>{v}</text>
          </g>
        )
      })}
      {/* Bars */}
      {data.labels.map((label, i) => {
        const x = marginL + i * groupW
        const achH = (data.achieved[i] / 5) * chartH
        const bmH = (data.benchmarks[i] / 5) * chartH
        const truncLabel = label.length > 12 ? label.slice(0, 11) + '…' : label
        return (
          <g key={i}>
            {/* Achieved */}
            <rect x={x + (groupW - 2 * barW - 4) / 2} y={marginT + chartH - achH}
              width={barW} height={achH} rx={4} fill="#1CA8E1" />
            {/* Benchmark */}
            <rect x={x + (groupW - 2 * barW - 4) / 2 + barW + 4} y={marginT + chartH - bmH}
              width={barW} height={bmH} rx={4} fill="#E2E8F0" />
            {/* Label */}
            <text x={x + groupW / 2} y={marginT + chartH + 14}
              textAnchor="middle" transform={`rotate(-30, ${x + groupW / 2}, ${marginT + chartH + 14})`}
              style={{ fontSize: '9px', fill: '#475569', fontFamily: 'Inter, sans-serif' }}>
              {truncLabel}
            </text>
          </g>
        )
      })}
      {/* Legend */}
      <g transform={`translate(${marginL + 20}, ${marginT + chartH + marginB - 12})`}>
        <rect x="0" y="-5" width="10" height="10" rx="2" fill="#1CA8E1" />
        <text x="14" y="4" style={{ fontSize: '11px', fill: '#0F172A', fontFamily: 'Inter, sans-serif' }}>Achieved Level</text>
        <rect x="120" y="-5" width="10" height="10" rx="2" fill="#E2E8F0" />
        <text x="134" y="4" style={{ fontSize: '11px', fill: '#0F172A', fontFamily: 'Inter, sans-serif' }}>{comparisonLabel || 'Benchmark Target'}</text>
      </g>
    </svg>
  )
}

// ── SVG Icons ────────────────────────────────────────────────────────────────

function PrinterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  )
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" />
      <line x1="17" y1="16" x2="23" y2="16" />
    </svg>
  )
}

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
  )
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

// ── Toggle Switch ────────────────────────────────────────────────────────────

function ToggleSwitch({
  checked,
  onChange,
  size = 'md',
}: {
  checked: boolean
  onChange: () => void
  size?: 'sm' | 'md'
}) {
  const h = size === 'sm' ? 'h-4 w-7' : 'h-5 w-9'
  const dot = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'
  const translate = size === 'sm' ? 'translate-x-3' : 'translate-x-4'
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative inline-flex ${h} shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}
      style={{ minWidth: size === 'sm' ? 28 : 36 }}
    >
      <span className={`pointer-events-none inline-block ${dot} transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? translate : 'translate-x-0'}`} />
    </button>
  )
}

// ── Section Toggle Definitions ───────────────────────────────────────────────

const SECTION_TOGGLES = [
  { key: 'description' as const, label: 'Description', desc: 'Initiative description text' },
  { key: 'duration' as const, label: 'Duration', desc: 'Estimated project duration' },
  { key: 'team_size' as const, label: 'Team Size', desc: 'Required team members' },
  { key: 'impact' as const, label: 'Impact Level', desc: 'Expected impact level' },
  { key: 'effort' as const, label: 'Effort Level', desc: 'Required effort level' },
  { key: 'business_value' as const, label: 'Business Value', desc: 'Business value narrative' },
  { key: 'budget_range' as const, label: 'Budget Range', desc: 'Estimated budget range' },
  { key: 'roi_estimate' as const, label: 'ROI Estimate', desc: 'Return on investment' },
  { key: 'technologies' as const, label: 'Technologies', desc: 'Required technologies' },
  { key: 'success_metrics' as const, label: 'Success Metrics', desc: 'Success criteria' },
  { key: 'recommended_tools' as const, label: 'Recommended Tools', desc: 'Suggested tools' },
  { key: 'phases' as const, label: 'Implementation Phases', desc: 'Phase-by-phase plan' },
] as const

// ── Main Component ───────────────────────────────────────────────────────────

export function ReportPage() {
  const { id } = useParams<{ id: string }>()
  const assessmentId = id ?? ''
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // ── Editable toggle state (initialized from URL params) ────────────────
  const [includeRecommendations, setIncludeRecommendations] = useState(
    () => searchParams.get('recommendations') !== 'false'
  )
  const [includeAnswers, setIncludeAnswers] = useState(
    () => searchParams.get('answers') !== 'false'
  )
  const [includeJustificationsYes, setIncludeJustificationsYes] = useState(
    () => searchParams.get('justifications_yes') !== 'false'
  )
  const [includeAnswerRecommendations, setIncludeAnswerRecommendations] = useState(
    () => searchParams.get('answer_recommendations') !== 'false'
  )
  const [qnaCols, setQnaCols] = useState({
    type: searchParams.get('col_type') !== 'false',
    level: searchParams.get('col_level') !== 'false',
    weight: searchParams.get('col_weight') !== 'false',
    score: searchParams.get('col_score') !== 'false'
  })
  const [sections, setSections] = useState(() => {
    const s = { ...DEFAULT_SECTIONS }
    for (const key of Object.keys(s) as (keyof typeof s)[]) {
      const val = searchParams.get(`sec_${key}`)
      if (val !== null) s[key] = val !== 'false'
    }
    return s
  })
  const toggleSection = (key: keyof typeof DEFAULT_SECTIONS) =>
    setSections(prev => ({ ...prev, [key]: !prev[key] }))

  // ── Side sheet state ───────────────────────────────────────────────────
  const [sheetOpen, setSheetOpen] = useState(false)

  // ── Data state ─────────────────────────────────────────────────────────
  const [data, setData] = useState<ReportData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch report data (re-fetches when top-level toggles change)
  useEffect(() => {
    if (!assessmentId) return
    setLoading(true)
    setError(null)
    resultsApi.getReportData(assessmentId, {
      include_recommendations: includeRecommendations,
      include_answers: includeAnswers,
    })
      .then(res => setData(res.data))
      .catch(err => setError(err?.response?.data?.error || err?.message || 'Failed to load report data'))
      .finally(() => setLoading(false))
  }, [assessmentId, includeRecommendations, includeAnswers])

  const handlePrint = useCallback(() => {
    setSheetOpen(false) // close sheet before printing
    setTimeout(() => window.print(), 150)
  }, [])

  const handleBack = useCallback(() => {
    navigate(`/assessments/${assessmentId}/results?tab=export`)
  }, [navigate, assessmentId])

  // ── Loading / Error states ─────────────────────────────────────────────
  if (loading) {
    return (
      <div className="report-loading">
        <div className="report-spinner" />
        <p>Preparing your report…</p>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="report-loading">
        <h2 style={{ color: '#EF4444' }}>Error Loading Report</h2>
        <p>{error || 'No data available.'}</p>
        <button className="report-btn" onClick={handleBack}>← Back to Results</button>
      </div>
    )
  }

  const { org, domains, initiatives, qna, chart_data } = data

  return (
    <>
      {/* ── Floating Toolbar (hidden during print) ─────────────────────── */}
      <div className="report-toolbar no-print">
        <div className="report-toolbar-inner">
          <button className="report-btn report-btn-ghost" onClick={handleBack}>
            <ArrowLeftIcon className="report-icon" /> Back to Results
          </button>
          <div className="report-toolbar-title">
            Report Preview — {org.name}
          </div>
          <div className="report-toolbar-actions">
            <button
              className="report-btn report-btn-outline"
              onClick={() => setSheetOpen(v => !v)}
            >
              <SettingsIcon className="report-icon" /> Settings
            </button>
            <button className="report-btn report-btn-primary" onClick={handlePrint}>
              <PrinterIcon className="report-icon" /> Print / Save as PDF
            </button>
          </div>
        </div>
      </div>

      {/* ── Settings Side Sheet (hidden during print) ──────────────────── */}
      {sheetOpen && <div className="report-sheet-overlay no-print" onClick={() => setSheetOpen(false)} />}
      <div className={`report-sheet no-print ${sheetOpen ? 'report-sheet-open' : ''}`}>
        <div className="report-sheet-header">
          <h3>Report Settings</h3>
          <button className="report-btn report-btn-ghost report-btn-icon" onClick={() => setSheetOpen(false)}>
            <XIcon className="report-icon" />
          </button>
        </div>
        <div className="report-sheet-body">
          {/* Include Recommendations */}
          <div className="sheet-toggle-group">
            <div className="sheet-toggle-row">
              <div>
                <div className="sheet-toggle-label">Include Recommendations</div>
                <div className="sheet-toggle-desc">Project initiatives based on maturity gaps</div>
              </div>
              <ToggleSwitch checked={includeRecommendations} onChange={() => setIncludeRecommendations(v => !v)} />
            </div>

            {includeRecommendations && (
              <div className="sheet-subsections">
                {SECTION_TOGGLES.map(({ key, label, desc }) => (
                  <div className="sheet-toggle-row sheet-toggle-sub" key={key}>
                    <div>
                      <div className="sheet-toggle-label-sm">{label}</div>
                      <div className="sheet-toggle-desc-sm">{desc}</div>
                    </div>
                    <ToggleSwitch checked={sections[key]} onChange={() => toggleSection(key)} size="sm" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Include Answers */}
          <div className="sheet-toggle-group">
            <div className="sheet-toggle-row">
              <div>
                <div className="sheet-toggle-label">Include Answers</div>
                <div className="sheet-toggle-desc">Question-by-question breakdown per domain</div>
              </div>
              <ToggleSwitch checked={includeAnswers} onChange={() => setIncludeAnswers(v => !v)} />
            </div>

            {includeAnswers && (
              <div className="sheet-subsections">
                <div className="sheet-toggle-row sheet-toggle-sub">
                  <div>
                    <div className="sheet-toggle-label-sm">Justifications</div>
                    <div className="sheet-toggle-desc-sm">Include answer-matching justification (Yes / No - Gap)</div>
                  </div>
                  <ToggleSwitch checked={includeJustificationsYes} onChange={() => setIncludeJustificationsYes((v: boolean) => !v)} size="sm" />
                </div>
                <div className="sheet-toggle-row sheet-toggle-sub">
                  <div>
                    <div className="sheet-toggle-label-sm">Recommendations</div>
                    <div className="sheet-toggle-desc-sm">Include answer-specific recommendations</div>
                  </div>
                  <ToggleSwitch checked={includeAnswerRecommendations} onChange={() => setIncludeAnswerRecommendations(v => !v)} size="sm" />
                </div>
                <div style={{ margin: '8px 0', borderTop: '1px solid #e2e8f0' }} />
                <div className="sheet-toggle-row sheet-toggle-sub">
                  <div>
                    <div className="sheet-toggle-label-sm">Question Type Column</div>
                  </div>
                  <ToggleSwitch checked={qnaCols.type} onChange={() => setQnaCols(p => ({ ...p, type: !p.type }))} size="sm" />
                </div>
                <div className="sheet-toggle-row sheet-toggle-sub">
                  <div>
                    <div className="sheet-toggle-label-sm">Level Column</div>
                  </div>
                  <ToggleSwitch checked={qnaCols.level} onChange={() => setQnaCols(p => ({ ...p, level: !p.level }))} size="sm" />
                </div>
                <div className="sheet-toggle-row sheet-toggle-sub">
                  <div>
                    <div className="sheet-toggle-label-sm">Weight Column</div>
                  </div>
                  <ToggleSwitch checked={qnaCols.weight} onChange={() => setQnaCols(p => ({ ...p, weight: !p.weight }))} size="sm" />
                </div>
                <div className="sheet-toggle-row sheet-toggle-sub">
                  <div>
                    <div className="sheet-toggle-label-sm">Score Column</div>
                  </div>
                  <ToggleSwitch checked={qnaCols.score} onChange={() => setQnaCols(p => ({ ...p, score: !p.score }))} size="sm" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Report Content ─────────────────────────────────────────────── */}
      <div className="report-root">

        {/* ─── PAGE 1: Cover + Org Info ───────────────────────────────── */}
        <div className="page">
          <div className="report-header">
            <img src="/vertowave.png" alt="Vertowave Logo" />
            <div className="meta">
              Report generated on {org.generated}<br />
              Powered by Verto Wave IT Maturity Assessment Platform
            </div>
          </div>

          <div className="hero-box">
            <h1>IT Maturity Assessment Report</h1>
            <div className="subtitle">{org.name} — {org.period}</div>
            <div className="hero-grid">
              <div className="hero-stat">
                <div className="label">Overall Maturity</div>
                <div className="value">Level {org.overall_level}</div>
                <div className="sub">{org.overall_name}</div>
              </div>
              <div className="hero-stat">
                <div className="label">{org.comparison_label || 'Industry Standard'}</div>
                <div className="value">{org.industry_target}</div>
                <div className="sub">Target Level</div>
              </div>
              <div className="hero-stat">
                <div className="label">Gap</div>
                <div className="value" style={{ color: '#FBBF24' }}>-{org.gap}</div>
                <div className="sub">Levels Below Target</div>
              </div>
            </div>
          </div>

          <section className="section">
            <h2>Organization Information</h2>
            <p style={{ fontSize: '.95rem', color: 'var(--text)', marginBottom: '1rem' }}>{org.description}</p>
            <div className="info-grid">
              <div className="info-item"><div className="label">Industry</div><div className="value">{org.industry}</div></div>
              <div className="info-item"><div className="label">Organization Size</div><div className="value">{org.size}</div></div>
              <div className="info-item"><div className="label">Address</div><div className="value">{org.address}</div></div>
              <div className="info-item"><div className="label">Website</div><div className="value">{org.website}</div></div>
              <div className="info-item"><div className="label">Assessment Created</div><div className="value">{org.created}</div></div>
              <div className="info-item"><div className="label">Completed</div><div className="value">{org.completed}</div></div>
              <div className="info-item"><div className="label">Created By</div><div className="value">{org.created_by}</div></div>
              <div className="info-item"><div className="label">Current Level Description</div><div className="value">{org.maturity_desc}</div></div>
            </div>
          </section>
        </div>

        {/* ─── PAGE 2: Domain Breakdown + Charts ─────────────────────── */}
        <div className="page">
          <section className="section">
            <h2>Results Overview — Domain Breakdown</h2>
            <div className="domain-grid">
              {domains.map(d => (
                <div className="domain-row" key={d.name}>
                  <div className="domain-name">{d.name}</div>
                  <div className="domain-level" style={{ background: d.gap === 'Below' ? '#EF4444' : 'var(--primary)' }}>
                    {d.level}
                  </div>
                  <div>
                    <div className="progress-track">
                      <div className="progress-fill" style={{ width: `${d.level_pct}%`, background: 'var(--primary)' }} />
                      <div style={{
                        position: 'absolute', left: `${d.bench_pct}%`, top: -2, bottom: -2,
                        width: 2, background: 'var(--secondary)', borderRadius: 1
                      }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.7rem', color: 'var(--text-secondary)', marginTop: '.2rem' }}>
                      <span>L{d.level} {d.level_name}</span>
                      <span>{org?.comparison_label || 'Benchmark'}: L{d.benchmark}</span>
                    </div>
                  </div>
                  <div className="domain-gap" style={{
                    background: d.gap === 'Below' ? '#FEE2E2' : d.gap === 'Close' ? '#FEF3C7' : '#DCFCE7',
                    color: d.gap === 'Below' ? '#EF4444' : d.gap === 'Close' ? '#F59E0B' : '#10B981',
                  }}>
                    {d.gap}
                  </div>
                </div>
              ))}
            </div>

            <div className="chart-row">
              <div className="chart-box">
                <h4>Maturity Radar</h4>
                <RadarChartSVG data={chart_data} comparisonLabel={org?.comparison_label} />
              </div>
              <div className="chart-box">
                <h4>Achieved vs {org?.comparison_label || 'Benchmark'}</h4>
                <BarChartSVG data={chart_data} comparisonLabel={org?.comparison_label} />
              </div>
            </div>
          </section>
        </div>

        {/* ─── PAGE 3+: Recommendations ──────────────────────────────── */}
        {includeRecommendations && initiatives.length > 0 && (
          <div className="page">
            <section className="section">
              <h2>Recommendations &amp; Project Initiatives</h2>
              <p className="intro-text">
                {initiatives.length} initiative{initiatives.length !== 1 ? 's have' : ' has'} been identified to mature your technology landscape.
              </p>

              {initiatives.map((init, idx) => (
                <div className="init-card" key={idx}>
                  <div className="init-header" style={{ borderLeft: `4px solid ${init.pc_color}` }}>
                    <h3>{init.title}</h3>
                    <div className="init-sub">{init.subtitle}</div>
                    <div className="init-tags">
                      <span className="tag tag-domain">{init.domain}</span>
                      <span className="tag tag-priority" style={{ background: init.pc_color }}>{init.priority} Priority</span>
                      <span className="tag tag-level">L{init.from_level} → L{init.to_level}</span>
                      <span className="tag tag-risk">{init.risk} Risk</span>
                    </div>
                  </div>
                  <div className="init-body">
                    {(sections.duration || sections.team_size || sections.impact || sections.effort) && (
                      <div className="init-meta">
                        {sections.duration && (
                          <div className="meta-chip">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                            {init.duration}
                          </div>
                        )}
                        {sections.team_size && (
                          <div className="meta-chip">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                            {init.team}
                          </div>
                        )}
                        {(sections.impact || sections.effort) && (
                          <div className="meta-chip">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
                            {sections.impact && sections.effort
                              ? `${init.impact} Impact / ${init.effort} Effort`
                              : sections.impact
                                ? `${init.impact} Impact`
                                : `${init.effort} Effort`
                            }
                          </div>
                        )}
                      </div>
                    )}

                    {sections.description && (
                      <>
                        <h4>Description</h4>
                        <p>{init.description}</p>
                      </>
                    )}

                    {sections.business_value && init.business_value && (
                      <>
                        <h4>Business Value</h4>
                        <p>{init.business_value}</p>
                      </>
                    )}

                    {sections.budget_range && (
                      <div className="kv-row">
                        <div className="kv-key">Budget Range</div>
                        <div className="kv-val">{init.budget}</div>
                      </div>
                    )}

                    {sections.roi_estimate && init.roi && (
                      <div className="kv-row">
                        <div className="kv-key">ROI Estimate</div>
                        <div className="kv-val">{init.roi}</div>
                      </div>
                    )}

                    {sections.technologies && init.technologies?.length > 0 && (
                      <div className="kv-row">
                        <div className="kv-key">Technologies</div>
                        <div className="kv-val">
                          <div className="pill-list">
                            {init.technologies.map((t, i) => <span className="pill" key={i}>{t}</span>)}
                          </div>
                        </div>
                      </div>
                    )}

                    {sections.success_metrics && init.success_metrics?.length > 0 && (
                      <div className="kv-row">
                        <div className="kv-key">Success Metrics</div>
                        <div className="kv-val">
                          <div className="pill-list">
                            {init.success_metrics.map((m, i) => <span className="pill" key={i}>{m}</span>)}
                          </div>
                        </div>
                      </div>
                    )}

                    {sections.recommended_tools && init.tools?.length > 0 && (
                      <div className="kv-row">
                        <div className="kv-key">Recommended Tools</div>
                        <div className="kv-val">
                          <div className="pill-list">
                            {init.tools.map((t, i) => <span className="pill" key={i}>{t}</span>)}
                          </div>
                        </div>
                      </div>
                    )}

                    {sections.phases && init.phases?.length > 0 && (
                      <>
                        <h4>Implementation Phases</h4>
                        <div className="timeline">
                          {init.phases.map((ph, pi) => (
                            <div className="phase" key={pi}>
                              <div className="phase-title">Phase {ph.num ?? pi + 1}: {ph.name}</div>
                              <div className="phase-desc">{ph.desc}</div>
                              <div className="phase-dur">{ph.duration}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </section>
          </div>
        )}

        {/* ─── Q&A Pages ─────────────────────────────────────────────── */}
        {includeAnswers && Object.keys(qna).length > 0 && (
          <div className="page page-qna">
            {Object.entries(qna).map(([domainName, domainData], idx) => {
              const qnaColSpan = 3 + (qnaCols.type ? 1 : 0) + (qnaCols.level ? 1 : 0) + (qnaCols.weight ? 1 : 0) + (qnaCols.score ? 1 : 0)
              return (
              <section className={`section qna-domain${idx > 0 ? ' qna-domain-subsequent' : ''}`} key={domainName}>
                <h2>{domainName} — Assessment Questions</h2>
                <p>{domainData.desc}</p>
                <div style={{ overflowX: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '40px', textAlign: 'center' }}>#</th>
                        <th>Question</th>
                        {qnaCols.type && <th>Type</th>}
                        {qnaCols.level && <th>Level</th>}
                        {qnaCols.weight && <th>Weight</th>}
                        <th>Answer</th>
                        {qnaCols.score && <th>Score</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {domainData.questions.map((q, qi) => (
                        <React.Fragment key={qi}>
                          <tr>
                            <td style={{ textAlign: 'center' }}><strong>{qi + 1}</strong></td>
                            <td>{q.question}</td>
                            {qnaCols.type && <td>{q.type}</td>}
                            {qnaCols.level && <td><span className="level-badge">{q.level}</span></td>}
                            {qnaCols.weight && <td>{q.weight}</td>}
                            <td className={q.answer === 'Yes' ? 'ans-yes' : 'ans-no'}>{q.answer}</td>
                            {qnaCols.score && <td className={q.score >= 1 ? 'score-1' : 'score-0'}>{q.score}</td>}
                          </tr>
                          {(includeJustificationsYes || includeAnswerRecommendations) ? (
                            <tr>
                              <td colSpan={qnaColSpan} style={{ padding: '8px 12px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '11px', color: '#475569', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                  {includeJustificationsYes && q.justification && (
                                    <div>
                                      <strong style={{ color: q.answer === 'Yes' ? '#16a34a' : '#dc2626' }}>
                                        Justification ({q.justification_label || (q.answer === 'Yes' ? 'Yes' : 'No - Gap')}):
                                      </strong> {q.justification}
                                    </div>
                                  )}
                                  {includeAnswerRecommendations && q.answer === 'No' && (
                                    <div>
                                      <strong style={{ color: '#2563eb' }}>Recommendation:</strong> {q.recommendation || <i style={{ color: '#94a3b8' }}>N/A</i>}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )})}
          </div>
        )}

      </div>
    </>
  )
}
